import { styled } from '@mui/material/styles';
import { Paper } from '@mui/material';
import {
  BusinessDayPeriodInterface,
  MarketPeriodInterface,
  ProductionPlanRevisionInterface,
  TimeSeriesInterface
} from 'features/dispatcher/dispatcherTypes';
import { I18N_HX_PLANNING_ROOT } from 'views/modules/Dispatcher/HxDashboard/HxPlanning/hxPlanningUtils';
import { DeviceInterface } from 'features/eai/eaiTypes';
import { MetricValueType } from 'utils/constants';
import { UnitType } from 'utils/characteristic';
import { getStyledTableBorder } from 'components/tables/styledTableParts';
import {
  ColumnsVisibilityModel,
  DataGridDensity,
  DataGridVisibility,
  HeaderFields
} from 'components/dataGrid/dataGridModels';

// ==============================|| H+X PLANNING CONTENT DEVICES UTILS ||============================== //

export enum HxPlanningDeviceColumn {
  NOMINATION = 'nomination',
  CURRENT_PLAN = 'currentPlan',
  PREDICTION = 'prediction',
  SOC_MWH = 'socMWh'
}

export enum BessMetric {
  SOC = 'soc',
  MWH = 'mwh'
}

export interface DeviceOrder {
  id: string;
  order: number;
}

export interface HxPlanningTableDeviceProps
  extends Omit<DataGridDensity, 'setDensity'>,
    Omit<DataGridVisibility, 'setColumnsVisibilityModel'> {
  device: DeviceInterface;
  index: number;
  productionPlanRevision: ProductionPlanRevisionInterface | undefined;
  onPlanChange: (
    deviceId: string,
    currentRevision: ProductionPlanRevisionInterface
  ) => void;
  setDeviceForRevisionHistoryDialog: (device: DeviceInterface | null) => void;
}

interface UpdateRevisionPeriodsProps {
  production?: string;
  periodNumber?: number;
  productionPlanRevision: ProductionPlanRevisionInterface;
}

export const I18N_HX_PLANNING_CONTENT_DEVICE_ROOT = `${I18N_HX_PLANNING_ROOT}.tables.device`;

export const HxDeviceDraggableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: getStyledTableBorder(theme)
}));

export const deviceHeaderFields: HeaderFields[] = [
  {
    name: HxPlanningDeviceColumn.NOMINATION,
    type: 'string',
    minWidth: 96
  },
  {
    name: HxPlanningDeviceColumn.CURRENT_PLAN,
    type: 'string',
    minWidth: 120
  },
  {
    name: HxPlanningDeviceColumn.PREDICTION,
    type: 'string',
    minWidth: 86
  },
  {
    name: HxPlanningDeviceColumn.SOC_MWH,
    type: 'string',
    minWidth: 116
  }
];

const WIDTH_NEEDED_FOR_BTN = 16;

export const getColumnMinWidth = (
  shouldRenderHistoryButton: boolean,
  minWidth?: number
) => {
  if (!minWidth) {
    return undefined;
  }

  return shouldRenderHistoryButton ? minWidth + WIDTH_NEEDED_FOR_BTN : minWidth;
};

const arePeriodsEqual = (
  previousPropsPeriods?: BusinessDayPeriodInterface[],
  nextPropsPeriods?: BusinessDayPeriodInterface[]
): boolean => {
  if (!previousPropsPeriods || !nextPropsPeriods)
    return previousPropsPeriods === nextPropsPeriods;
  return (
    previousPropsPeriods.length === nextPropsPeriods.length &&
    previousPropsPeriods.every(
      (item, index) => item === nextPropsPeriods[index]
    )
  );
};

const areColumnVisibilityModelsEqual = (
  previousColumnVisibility?: ColumnsVisibilityModel,
  nextColumnVisibility?: ColumnsVisibilityModel
): boolean => {
  return (
    JSON.stringify(previousColumnVisibility) ===
    JSON.stringify(nextColumnVisibility)
  );
};

export const areHxPlanningContentDevicePropsEqual = (
  prevProps: Readonly<HxPlanningTableDeviceProps>,
  nextProps: Readonly<HxPlanningTableDeviceProps>
): boolean => {
  return (
    Object.entries(prevProps) as [keyof HxPlanningTableDeviceProps, unknown][]
  ).every(([key, value]) => {
    if (key === 'columnsVisibilityModel') {
      return areColumnVisibilityModelsEqual(prevProps[key], nextProps[key]);
    }
    if (key === 'productionPlanRevision') {
      return arePeriodsEqual(prevProps[key]?.periods, nextProps[key]?.periods);
    }
    if (key === 'onPlanChange') return true;
    return value === nextProps[key];
  });
};

export const getNewDeviceListOrder = (
  deviceList: DeviceInterface[],
  existingDeviceOrderList: DeviceOrder[]
) =>
  deviceList.reduce((acc: DeviceOrder[], device) => {
    const deviceId = device.id.toString();
    if (!existingDeviceOrderList?.some((order) => order.id === deviceId)) {
      acc.push({
        id: deviceId,
        order: existingDeviceOrderList?.length ?? 0
      });
    }

    return acc;
  }, []);

export const orderDeviceListByStoredOrder = (
  deviceList: DeviceInterface[],
  orderList: DeviceOrder[]
) => {
  const itemOrderMap = new Map<string | bigint, number>();
  orderList.forEach((item) => itemOrderMap.set(item.id, item.order));

  return [...deviceList].sort((a, b) => {
    const aOrder = itemOrderMap.get(a.id.toString()) ?? Number.MAX_VALUE;
    const bOrder = itemOrderMap.get(b.id.toString()) ?? Number.MAX_VALUE;
    return aOrder - bOrder;
  });
};

export const orderDeviceListByNames = (
  deviceList: DeviceInterface[]
): DeviceInterface[] => {
  return [...deviceList].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
};

export const getHxDeviceMetric = (
  timeSeriesList: TimeSeriesInterface[],
  metricType: MetricValueType,
  deviceId: string
) => {
  return timeSeriesList.find((series) => {
    return (
      series.metric.type === metricType &&
      series.monitoredResource.labels?.some((label) => label.value === deviceId)
    );
  });
};

export const getCurrentPlan = (
  period: MarketPeriodInterface,
  productionPlanRevision?: ProductionPlanRevisionInterface
) => {
  return productionPlanRevision?.periods.find(
    (revisionPeriod) => revisionPeriod.periodNumber === period.timeUnitNumber
  )?.production;
};

export const getNomination = (
  period: MarketPeriodInterface,
  nominatedRevisionPeriods?: BusinessDayPeriodInterface[]
) => {
  return nominatedRevisionPeriods?.find(
    (revisionPeriod) => revisionPeriod.periodNumber === period.timeUnitNumber
  )?.production;
};

export const getPrediction = (
  periodTimestamp: number,
  timeSeries?: TimeSeriesInterface
) => {
  return timeSeries?.dataPoints?.find((dataPoint) => {
    return (
      new Date(dataPoint.interval.dateTimeFrom).valueOf() === periodTimestamp
    );
  })?.value?.doubleValue;
};

export const updateRevisionPeriods = ({
  periodNumber,
  productionPlanRevision,
  production
}: UpdateRevisionPeriodsProps): ProductionPlanRevisionInterface => {
  return {
    ...productionPlanRevision,
    periods: productionPlanRevision.periods.map((period) => {
      if (period.periodNumber === periodNumber) {
        const newPeriod: BusinessDayPeriodInterface = {
          ...period,
          production: production ?? ''
        };

        return newPeriod;
      }
      return period;
    })
  };
};

export const createNewProductionPlanPeriods = (
  marketPeriodsForMarketDay?: MarketPeriodInterface[]
) =>
  marketPeriodsForMarketDay?.map((period) => {
    const newPeriod: BusinessDayPeriodInterface = {
      periodNumber: period.timeUnitNumber,
      production: null,
      unit: UnitType.MWH
    };

    return newPeriod;
  }) ?? [];
